import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSocket } from '../hooks/useSocket';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../contexts/ThemeContext';
import { API_BASE_URL } from './config/api';
import { formatEuro } from '../utils/formatEuro';
import { openDeliveryForCall } from '../utils/deliveryCallIntent';
import { lastDeliveryOrder, summarizeOrderItems, SummaryItem } from '../utils/deliveryRepeat';
import {
  Phone, X, ExternalLink, MapPin, RefreshCw, Plus
} from 'lucide-react';

interface IncomingCallData {
  caller_phone: string;
  caller_name: string | null;
  destination: string | null;
  call_uuid: string;
  event_type: string;
  customer: {
    id: number;
    name: string;
    phone: string;
    notes: string;
    total_orders: number;
    total_spent: number;
    loyalty_points: number;
    addresses: CallAddress[];
    recent_orders: CallOrder[];
  } | null;
  timestamp: string;
}

// services/callerIdService.js: διευθύνσεις (προεπιλεγμένη πρώτα) και οι 5 τελευταίες παραγγελίες.
interface CallAddress {
  id: number;
  street_address?: string | null;
  area?: string | null;
}
interface CallOrder {
  id: number;
  short_code?: string | null;
  total_amount?: number | string | null;
  order_type?: string | null;
  created_at?: string | null;
  status?: string | null;
  delivery_address_id?: number | null;
}
// Τα είδη της τελευταίας παραγγελίας delivery (GET /api/orders/:storeId/order/:id) — μόνο για εμφάνιση.
type OrderPreview =
  | { state: 'loading' }
  | { state: 'ready'; items: SummaryItem[]; street?: string | null; area?: string | null }
  | { state: 'error' };

const IncomingCallPopup: React.FC = () => {
  const { t } = useTranslation(['delivery', 'common']);
  const { socket, isConnected } = useSocket();
  const { store } = useAuth();
  // «Νέα εμφάνιση» (ανά συσκευή): η τελευταία παραγγελία delivery και «Ξανά τα ίδια».
  const { look } = useTheme();
  const v2 = look === 'v2';
  const [calls, setCalls] = useState<IncomingCallData[]>([]);
  const [previews, setPreviews] = useState<Record<number, OrderPreview>>({});

  // Κλήσεις που έκλεισε ο ταμίας (ή άνοιξε): η επανάληψη «ό,τι χάθηκε» (focus/επανασύνδεση, 90″)
  // δεν τις ξαναβγάζει — χωρίς φόρτωση σελίδας η αναγγελία μένει ζωντανή και θα ξαναεμφανιζόταν.
  const dismissedRef = useRef(new Set<string>());

  // Stable ref to the addCall logic so the polling effects can use it without
  // recreating themselves and resetting the socket listeners on every render.
  const addCall = useCallback((data: IncomingCallData, fromReplay: boolean) => {
    if (fromReplay && dismissedRef.current.has(data.call_uuid)) return;
    // Skip the ring sound for replayed calls — they're not happening *now*,
    // they're calls we missed while disconnected; the visual popup is enough.
    if (!fromReplay) {
      try {
        const audio = new Audio('/ring.mp3');
        audio.volume = 0.7;
        audio.play().catch(() => {});
      } catch (e) { /* no sound file, that's fine */ }
    }

    setCalls(prev => {
      const existing = prev.find(c => c.call_uuid === data.call_uuid);
      if (existing) {
        return prev.map(c => c.call_uuid === data.call_uuid ? data : c);
      }
      return [data, ...prev].slice(0, 5);
    });
  }, []);

  // Replay any calls we may have missed while the socket was disconnected.
  // socket.io drops events when a client is offline, so without this the
  // cashier silently misses calls that came in during a tab sleep / network
  // blip / server restart.
  const fetchRecentCalls = useCallback(async () => {
    if (!store?.id) return;
    try {
      const token = localStorage.getItem('xentrix_token');
      const r = await fetch(
        `${API_BASE_URL}/api/yuboto/stores/${store.id}/recent-calls?seconds=90`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!r.ok) return;
      const { calls: recent } = await r.json();
      if (!Array.isArray(recent)) return;
      for (const c of recent) addCall(c as IncomingCallData, true);
    } catch (_) { /* best effort — silent */ }
  }, [store?.id, addCall]);

  // Listen for incoming-call events
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleIncomingCall = (data: IncomingCallData) => {
      console.log('📞 Incoming call:', data);
      addCall(data, false);
    };

    const handleCallEnded = (data: { call_uuid: string }) => {
      console.log('📞 Call ended:', data);
      setCalls(prev => prev.filter(c => c.call_uuid !== data.call_uuid));
    };

    socket.on('incoming-call', handleIncomingCall);
    socket.on('call-ended', handleCallEnded);
    return () => {
      socket.off('incoming-call', handleIncomingCall);
      socket.off('call-ended', handleCallEnded);
    };
  }, [socket, isConnected, addCall]);

  // Trigger replay on (re)connect — whenever the socket transitions to
  // connected, fetch what we may have missed in the offline window.
  useEffect(() => {
    if (isConnected) fetchRecentCalls();
  }, [isConnected, fetchRecentCalls]);

  // Replay on tab focus / visibility change — browsers throttle background
  // tabs aggressively, so a returning cashier is the most common case for
  // missed calls. Also covers the case where the socket appears connected
  // but the OS suspended the page mid-call.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') fetchRecentCalls(); };
    const onFocus = () => fetchRecentCalls();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchRecentCalls]);

  // Auto-dismiss calls after 90 seconds
  useEffect(() => {
    if (calls.length === 0) return;
    const timer = setInterval(() => {
      const cutoff = Date.now() - 90000;
      setCalls(prev => prev.filter(c => new Date(c.timestamp).getTime() > cutoff));
    }, 5000);
    return () => clearInterval(timer);
  }, [calls.length]);

  const dismissCall = useCallback((uuid: string) => {
    dismissedRef.current.add(uuid);
    setCalls(prev => prev.filter(c => c.call_uuid !== uuid));
  }, []);

  const dismissAll = useCallback(() => {
    setCalls(prev => {
      prev.forEach(c => dismissedRef.current.add(c.call_uuid));
      return [];
    });
  }, []);

  // Στη σελίδα delivery ΜΕΣΑ στην εφαρμογή (χωρίς φόρτωση σελίδας): το App αλλάζει σελίδα και
  // το DeliveryWithMap ανοίγει τον πελάτη με αυτό το τηλέφωνο (utils/deliveryCallIntent).
  const navigateToCustomer = useCallback((phone: string) => {
    openDeliveryForCall({ phone });
  }, []);

  // «Ξανά τα ίδια»: ο πελάτης, η διεύθυνση εκείνης της παραγγελίας και τα είδη της στο καλάθι
  // (ίδια επανάληψη με την καρτέλα πελάτη: ιστορικές τιμές, P1-Q9), έτοιμα για πληρωμή.
  const repeatLastOrder = useCallback((phone: string, order: CallOrder) => {
    openDeliveryForCall({ phone, reorderOrderId: order.id, addressId: order.delivery_address_id ?? null });
  }, []);

  // Νέα εμφάνιση: τα είδη της τελευταίας παραγγελίας delivery κάθε γνωστού καλούντα (μία φορά).
  const requestedPreviewsRef = useRef(new Set<number>());
  useEffect(() => {
    if (!v2 || !store?.id) return;
    for (const call of calls) {
      const order = lastDeliveryOrder(call.customer?.recent_orders);
      if (!order || requestedPreviewsRef.current.has(order.id)) continue;
      requestedPreviewsRef.current.add(order.id);
      setPreviews(prev => ({ ...prev, [order.id]: { state: 'loading' } }));
      (async () => {
        try {
          const r = await fetch(`${API_BASE_URL}/api/orders/${store.id}/order/${order.id}`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}` }
          });
          if (!r.ok) throw new Error(String(r.status));
          const data = await r.json();
          setPreviews(prev => ({
            ...prev,
            [order.id]: { state: 'ready', items: Array.isArray(data.items) ? data.items : [], street: data.street_address, area: data.area },
          }));
        } catch {
          setPreviews(prev => ({ ...prev, [order.id]: { state: 'error' } }));
        }
      })();
    }
  }, [v2, store?.id, calls]);

  const formatOrderDate = (iso?: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' })}`;
  };

  const formatPhone = (phone: string) => {
    if (!phone) return '';
    const clean = phone.replace(/^\+30/, '').replace(/^\+/, '');
    if (clean.length === 10) {
      return `${clean.slice(0, 3)} ${clean.slice(3, 6)} ${clean.slice(6)}`;
    }
    return clean;
  };

  const formatMoney = (n: number) => n.toFixed(2) + '€';

  if (calls.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm w-full">
      {calls.length > 1 && (
        <button
          onClick={dismissAll}
          className="self-end text-xs text-t3 hover:text-t1 px-2 py-1 rounded bg-surface-1/80 backdrop-blur"
        >
          {t('closeAll')} ({calls.length})
        </button>
      )}

      {calls.map(call => {
        const cust = call.customer;
        return (
          <div
            key={call.call_uuid}
            className={`rounded-xl shadow-2xl border backdrop-blur overflow-hidden transition-all duration-300 ${
              cust
                ? 'bg-surface-1/95 border-emerald-500/50'
                : 'bg-surface-1/95 border-amber-500/50'
            }`}
          >
            {/* Header row */}
            <div className="flex items-center gap-3 px-4 py-3">
              {/* Animated phone icon */}
              <div className={`relative flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center ${
                cust ? 'bg-emerald-500/20' : 'bg-amber-500/20'
              }`}>
                <Phone className={`h-5 w-5 ${cust ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-700 dark:text-amber-400'} animate-pulse`} />
                <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full animate-ping" />
                <span className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-t1 truncate">
                    {cust ? cust.name : t('unknownNumber')}
                  </span>
                  {cust && cust.total_orders > 0 && (
                    <span className="text-xs bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded-full flex-shrink-0">
                      {cust.total_orders} {t('orders_short')}
                    </span>
                  )}
                </div>
                <div className="text-xs text-t3 font-mono">
                  {formatPhone(call.caller_phone)}
                </div>
                {cust && cust.total_spent > 0 && (
                  <div className="text-[10px] text-t4">
                    {t('common:total')}: {formatMoney(cust.total_spent)}
                  </div>
                )}
              </div>

              <button
                onClick={() => dismissCall(call.call_uuid)}
                aria-label={t('common:close')}
                className={`${v2 ? 'w-11 h-11 -mr-2 rounded-lg flex items-center justify-center' : 'p-1'} text-t4 hover:text-red-600 dark:hover:text-red-400 transition-colors flex-shrink-0`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {v2 && cust ? (() => {
              // Νέα εμφάνιση, γνωστός πελάτης: η τελευταία παραγγελία delivery + «Ξανά τα ίδια» / «Νέα παραγγελία».
              const last = lastDeliveryOrder(cust.recent_orders);
              const preview = last ? previews[last.id] : undefined;
              const addr = last ? (cust.addresses || []).find(a => Number(a.id) === Number(last.delivery_address_id)) : undefined;
              const ready = preview?.state === 'ready' ? preview : null;
              const street = addr?.street_address || ready?.street || null;
              const area = addr?.area || ready?.area || null;
              const summary = ready ? summarizeOrderItems(ready.items) : null;
              return (
                <div className="px-4 pb-3 space-y-2.5">
                  {last && (
                    <div data-testid="call-last-order" className="rounded-lg border border-line bg-surface-2 px-3 py-2.5 space-y-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs text-t3 truncate">
                          {t('delivery:v2.lastDelivery')}{formatOrderDate(last.created_at) ? ` · ${formatOrderDate(last.created_at)}` : ''}
                        </span>
                        <span className="text-sm font-semibold text-t1 tabular-nums shrink-0">{formatEuro(Number(last.total_amount) || 0)}</span>
                      </div>
                      <p className="text-sm text-t1 leading-snug line-clamp-2 break-words">
                        {summary
                          ? `${summary.text || '—'}${summary.more > 0 ? ` ${t('delivery:v2.moreItems', { count: summary.more })}` : ''}`
                          : preview?.state === 'error' ? t('delivery:v2.itemsUnavailable') : '…'}
                      </p>
                      {street && (
                        <p className="text-xs text-t3 flex items-center gap-1 min-w-0">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate">{street}{area ? `, ${area}` : ''}</span>
                        </p>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2">
                    {last && (
                      <button
                        type="button"
                        data-testid="call-repeat"
                        onClick={() => { repeatLastOrder(call.caller_phone, last); dismissCall(call.call_uuid); }}
                        className="flex-1 h-11 rounded-lg bg-brand text-brand-fg text-sm font-semibold flex items-center justify-center gap-1.5 hover:brightness-110 active:scale-[0.98] transition-all"
                      >
                        <RefreshCw className="h-4 w-4" /> {t('delivery:v2.repeat')}
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid="call-new-order"
                      onClick={() => { navigateToCustomer(call.caller_phone); dismissCall(call.call_uuid); }}
                      className={`flex-1 h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 active:scale-[0.98] transition-all ${
                        last ? 'border border-line bg-surface-2 text-t1 hover:bg-surface-3' : 'bg-brand text-brand-fg hover:brightness-110'
                      }`}
                    >
                      <Plus className="h-4 w-4" /> {t('delivery:v2.newOrder')}
                    </button>
                  </div>
                </div>
              );
            })() : (
            /* Action buttons — κλασική εμφάνιση, και άγνωστος αριθμός (φόρμα νέου πελάτη με το τηλέφωνο) */
            <div className="flex gap-2 px-4 pb-3">
              <button
                data-testid="call-navigate"
                onClick={() => {
                  navigateToCustomer(call.caller_phone);
                  dismissCall(call.call_uuid);
                }}
                className={`cashier-primary flex-1 flex items-center justify-center gap-1.5 px-3 ${v2 ? 'h-11 text-sm bg-brand text-brand-fg hover:brightness-110' : 'py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white'} rounded-lg font-medium transition-colors`}
              >
                <ExternalLink className="h-3.5 w-3.5" /> {t('navigate')}
              </button>
              <button
                onClick={() => dismissCall(call.call_uuid)}
                className={`flex items-center justify-center gap-1.5 px-3 ${v2 ? 'h-11 text-sm' : 'py-2 text-xs'} bg-surface-2 hover:bg-surface-3 text-t2 rounded-lg font-medium transition-colors`}
              >
                <X className="h-3.5 w-3.5" /> {t('common:close')}
              </button>
            </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default IncomingCallPopup;
